import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { DownloadManagerItemState, DownloadMode, DownloadGroupStatus, DownloadGroupName, DownloadStatus } from '../reducer';
import NativeApi from '../../../utils/nativeApi';
import UserAgentUtils from '../../../utils/userAgent';
import DriveItemUtils from '../../../utils/driveItem';
import { downloadsFolder, tempFolder } from '../../../utils';
import { Api } from '../../../typings/api';
import { createNotification } from '../../NotificationBar/actions';
import { getOrFetchDriveItemMeta } from '../../DriveItemList/actions';
import DownloadManagerUtils from '../../../utils/downloadManager';
import {
	updateDownload,
	removeDownloadItem,
	removeDependentFromDependencies,
	setIsDeletingAllStatus,
	setDownloadGroupStatus,
	updateDownloadStatus,
	setIsMarkingItemForDeletion,
	resetDownloads,
} from './store';
import IndexedDBManager from '../../../utils/indexedDB/manager';
const Q = require('q');

const emptyDownloadCache = () => (dispatch: Dispatch<StoreState>): Q.Promise<{} | void> => {
	return NativeApi.existDirectory(tempFolder)
		.then(directoryExist => directoryExist ? NativeApi.deleteDirJs(`../../${tempFolder}`) : {});
};

const deleteItem = (driveItem: Api.DriveItem, isDeletionOnStartUp = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		if (!driveItem) {
			console.error('Trying to delete a non-existent drive item');
			// Trying to delete a drive item which was downloaded but is no longer in the app (e.g. removed from Admin Area)
			return Q.Promise.reject();
		}

		const { downloadManager, driveList: { resolutions } } = getState();
		const { associatedResolutions } = driveItem;
		const driveItemInDownloadManager = downloadManager.items[driveItem.id];
		let promise: Q.Promise<{} | void> = Q.Promise.resolve();

		if (driveItemInDownloadManager) {
			const { dependencies, dependents } = driveItemInDownloadManager;

			// Mark the resource and its dependencies that the deletion has started in case the app is closed before the deletion has ended
			if (driveItemInDownloadManager.mode === DownloadMode.Requested && !isDeletionOnStartUp) {
				promise = promise
					.then(() => dispatch(markPersistentItemForDeletion(driveItem.id)));
			}

			const isDependencyWithDependents = driveItemInDownloadManager.mode === DownloadMode.Dependency && dependents.length > 0;
			const isDependencyOfPersistentDriveItem = DownloadManagerUtils.isDependencyOfPersistentDriveItem(downloadManager.items, driveItem.id, [driveItem.id]);

			if ((isDependencyOfPersistentDriveItem || isDependencyWithDependents) && !isDeletionOnStartUp) {
				if (driveItemInDownloadManager.mode === DownloadMode.Requested) {
					dispatch(updateDownload(driveItem, DownloadMode.Dependency, dependencies, dependents));
				}

				return Q.Promise.resolve();
			} else {
				if (associatedResolutions) {
					const availableResolutionsId = associatedResolutions[driveItem.id] || 'default';

					if (resolutions[availableResolutionsId]) {
						const driveItemSizes = resolutions[availableResolutionsId].sizes;
						if (driveItemSizes) {
							driveItemSizes.forEach((size) => {
								const fileName = `${driveItem.id}${size.box ? '_box-' : '_'}${size.width}x${size.height}.${size.extension}`;
								nativeDeleteFile(fileName);
							});
						}
					}
				}

				promise = promise
					.then(() => dispatch(removeDependentFromDependencies(driveItem)))
					.then(() => dispatch(deleteDependencies(dependencies)))
					.then(() => dispatch(nativeDelete(driveItem)))
					.then(() => dispatch(removeDownloadItem(driveItem.id)))
					.catch(error => {
						console.error(`Error deleting item ${driveItem.id}:`, error);

						return dispatch(createNotification('error.delete_fail', { id: driveItem.id }));
					});

				return promise;
			}
		}
		return dispatch(nativeDelete(driveItem));
	};

const deleteDependencies = (ids: number[]) => (
	dispatch: Dispatch<StoreState>,
	getState: () => StoreState
): Q.Promise<{} | void> => {
	if (!ids || ids.length === 0) {
		return Q.Promise.resolve();
	}

	const { driveList, downloadManager } = getState();
	const { driveItems } = driveList;
	const { items } = downloadManager;

	return ids.reduce((promiseChain: Q.Promise<{} | void>, dependencyId) => {
		if (items[dependencyId] && !DownloadManagerUtils.isPersistentItem(items[dependencyId])) {
			return promiseChain.then(() => dispatch(deleteItem(driveItems[dependencyId])));
		}

		return promiseChain;
	}, Q.Promise.resolve());
};

const nativeDeleteFile = (fileName) => {
	return NativeApi.existFile(`${downloadsFolder}/${fileName}`)
		.then(fileExist => fileExist ? NativeApi.removeFile(`${downloadsFolder}/${fileName}`) : {});
};

const nativeDeleteFolder = (id) => {
	return NativeApi.existDirectory(`${downloadsFolder}/${id}`)
		.then(directoryExist => directoryExist ? NativeApi.deleteDirJs(`../../${downloadsFolder}/${id}`) : {});
};

const nativeDelete = (driveItem: Api.DriveItem) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		return NativeApi.existFile(`${downloadsFolder}/${driveItem.id}.zip`)
			.then(fileExist => fileExist ? NativeApi.removeFile(`${downloadsFolder}/${driveItem.id}.zip`) : {})
			.then(() => {
				switch (driveItem.type) {
					case Api.DriveItemType.Directory:
					case Api.DriveItemType.Publication:
					case Api.DriveItemType.Component:
					case Api.DriveItemType.Template:
					case Api.DriveItemType.Gallery:
						return nativeDeleteFolder(driveItem.id);
					case Api.DriveItemType.Movie:
					case Api.DriveItemType.File:
						nativeDeleteFolder(driveItem.id); // Remove the folder containing the cover image
					default:
						const fileName = DriveItemUtils.getDownloadedFileName(driveItem);
						return nativeDeleteFile(fileName);
				}
			});
	};

const deletePendingDownloads = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | void> => {
	const { downloadManager } = getState();
	const { items } = downloadManager;
	let promises: Array<Promise<{} | void>> = [];

	Object.keys(items).forEach(key => {
		const item: DownloadManagerItemState = items[key];
		const id = parseInt(key, 10);

		if ((item.mode === DownloadMode.Required || item.mode === DownloadMode.Requested) && !DownloadManagerUtils.isItemDownloaded(downloadManager, id)) {
			promises.push(
				dispatch(getOrFetchDriveItemMeta(id, false)).then((driveItem: Api.DriveItem) => dispatch(deleteItem(driveItem)))
			);
		}
	});
	return Q.Promise.all(promises);
};

const removeAllDownloads = () =>
	(dispatch: Dispatch<StoreState>): void => {
	dispatch(setIsDeletingAllStatus(true));
	if (UserAgentUtils.isAndroidApp()) {
		NativeApi.existDirectory(`${tempFolder}`)
			.then(directoryExist => directoryExist ? NativeApi.deleteDirJs(`../../${tempFolder}`) : {})
			.catch(error => dispatch(createNotification('error.delete_fail')));
	}

	dispatch(deleteAllNonRequiredItems())
		.finally(() => dispatch(setIsDeletingAllStatus(false)))
		.then(() => dispatch(setDownloadGroupStatus(DownloadGroupName.ALL, DownloadGroupStatus.NotStarted)))
		.then(() => dispatch(setDownloadGroupStatus(DownloadGroupName.IMPORTANT, DownloadGroupStatus.NotStarted)))
		.then(() => dispatch(setDownloadGroupStatus(DownloadGroupName.SELECTED, DownloadGroupStatus.NotStarted)))
		.then(() => dispatch(createNotification('info.delete_all')))
		.catch(error => dispatch(createNotification('error.delete_fail')));
};

const deleteAllNonRequiredItems = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void | {}[]> => {
	const { downloadManager, driveList } = getState();
	const { items } = downloadManager;
	const { driveItems } = driveList;

	let promises: Q.Promise<{} | void>[] = [];

	Object.keys(items).forEach(id => {
		if (items[id].mode !== DownloadMode.Required) {
			promises.push(dispatch(deleteItem(driveItems[id])));
		}
	});

	return Q.allSettled(promises);
};

// Needed for deletion recovery on app restart
const markPersistentItemForDeletion = (
	driveItemId: number,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | void> => {
	dispatch(setIsMarkingItemForDeletion(true));

	const { downloadManager } = getState();
	const driveItemInDownloadManager = downloadManager.items[driveItemId];

	if (driveItemInDownloadManager) {
		await dispatch(updateDownloadStatus(driveItemId, DownloadStatus.STARTING_DELETION));

		const { dependencies } = driveItemInDownloadManager;

		if (!dependencies || dependencies.length === 0) {
			dispatch(setIsMarkingItemForDeletion(false));
			return;
		}

		await dependencies.reduce(async (previousPromise, dependencyId) => {
			await previousPromise;

			const dependencyInDownloadManager = downloadManager.items[dependencyId];
			const { dependents: dependencyDependents } = dependencyInDownloadManager;
			const isDependencyWithDependents = dependencyInDownloadManager.mode === DownloadMode.Dependency && dependencyDependents.length > 1;
			const isDependencyOfPersistentDriveItem = DownloadManagerUtils.isDependencyOfPersistentDriveItem(downloadManager.items, dependencyId, [dependencyId, driveItemId]);

			return isDependencyOfPersistentDriveItem || isDependencyWithDependents
				? Promise.resolve()
				: dispatch(updateDownloadStatus(dependencyId, DownloadStatus.STARTING_DELETION));
		}, Promise.resolve());
	} else {
		dispatch(setIsMarkingItemForDeletion(false));
		return Promise.resolve();
	}

	dispatch(setIsMarkingItemForDeletion(false));
};

const resetAllDownloads = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {
	dispatch(setIsDeletingAllStatus(true));

	// Call native resetDownloads method
	return NativeApi.resetDownloads()
		.then(() => {
			// Remove all download items from state
			dispatch(resetDownloads());

			// Clear IndexedDB downloads
			return IndexedDBManager.setDownloads([]);
		})
		.then(() => {
			dispatch(setIsDeletingAllStatus(false));
			dispatch(createNotification('info.reset_all_downloads'));
		})
		.catch(error => {
			dispatch(setIsDeletingAllStatus(false));
			dispatch(createNotification('error.reset_fail'));
			console.error('Error resetting downloads:', error);
		});
};

export { emptyDownloadCache, deletePendingDownloads, deleteItem, nativeDelete, removeAllDownloads, resetAllDownloads };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/delete.ts